import {
  useGetVisaDocument,
  useGetVisaDocuments,
  VisaDocumentLiteModel,
  VisaDocumentModel,
} from 'api-hooks/visa-document';
import { OnChangeParams } from 'baseui/select';
import { ModelTransformer } from 'common/helpers/common';
import { Input } from 'components/elements';
import { visaOptionsTransformer } from 'hooks/use-get-visa-column';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {
  name: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  noMargin?: boolean;
  disabled?: boolean;
}

export default function VisaSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:visa'), placeholder = t('common:select_visa') } =
    props;

  const { control, setValue } = useFormContext();
  const value = useWatch({
    control,
    name: props.name,
  });

  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetVisaDocuments,
    useDetailLazyQueryHook: useGetVisaDocument,
    listTransformer: (result) => {
      return result.data.map((v) => {
        const data = ModelTransformer(v, VisaDocumentLiteModel);
        return visaOptionsTransformer(data);
      });
    },
    detailTransformer: (result) => {
      const data = ModelTransformer(result.data, VisaDocumentModel);
      return visaOptionsTransformer(data);
    },
    paginationTransformer: (result) => {
      return result.meta;
    },
    getMemoizedListVariables: (page, q) => ({
      params: {
        q,
        page,
      },
    }),
    getMemoizedDetailVariables: (value: string) => ({
      id: value,
    }),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      disabled={props.disabled}
      {...selectProps}
    />
  );
}
